// doctor-app/src/hooks/usePatientQueue.js
//
// Manages the doctor-side state:
//   - Polls the backend every 5 s for waiting/in-progress patients
//   - Handles admitting a patient (joining the Chime meeting as DOCTOR)
//   - Handles ending the call (deletes Chime meeting, marks session ENDED)

import { useState, useEffect, useCallback, useRef } from "react";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { MeetingSessionConfiguration } from "amazon-chime-sdk-js";

const API           = 'https://pzfesc4g10.execute-api.us-east-1.amazonaws.com/prod'; //process.env.VITE_API_URL;
const DOCTOR_ID     = 'doctor-001'; //process.env.VITE_DOCTOR_ID ?? "doctor-001";
const POLL_INTERVAL = 5_000; // ms
const APP_URL = 'https://drsadhanakumar.com'; //process.env.VITE_APP_URL;

export function usePatientQueue() {
  const meetingManager = useMeetingManager();

  const [sessions,     setSessions]     = useState([]);
  const [activeSession, setActive]      = useState(null);
  const [callStatus,   setCallStatus]   = useState("idle"); // idle|connecting|in-call
  const [queueError,   setQueueError]   = useState(null);
  const [callError,    setCallError]    = useState(null);

  const pollRef = useRef(null);

  // ── Fetch waiting queue ───────────────────────────────────────────────
  const fetchQueue = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/sessions?doctorId=${DOCTOR_ID}`);
      if (!res.ok) throw new Error(`Queue fetch failed: ${res.status}`);
      const data = await res.json();
      setSessions(data.sessions ?? []);
      setQueueError(null);
    } catch (err) {
      setQueueError(err.message);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    pollRef.current = setInterval(fetchQueue, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchQueue]);

  // ── Admit patient — doctor clicks "Start Visit" ───────────────────────
  const admitPatient = useCallback(async (session) => {
    setCallError(null);
    setCallStatus("connecting");
    setActive(session);

    try {
      const res  = await fetch(`${API}/sessions/join`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ sessionId: session.sessionId, role: "DOCTOR" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to join meeting");

      const configuration = new MeetingSessionConfiguration(
        data.meeting,   // { Meeting: { MeetingId, MediaPlacement, ... } }
        data.attendee,  // { Attendee: { AttendeeId, JoinToken, ... } }
      );

      await meetingManager.join(configuration);

      // Select default devices (mic + camera) — required before start()
      await meetingManager.listAndSelectDevices();

      await meetingManager.start();

      // Stop polling while in a call to reduce noise
      clearInterval(pollRef.current);

      setCallStatus("in-call");
    } catch (err) {
      setCallError(err.message);
      setCallStatus("idle");
      setActive(null);
    }
  }, [meetingManager]);

  // ── Force-end a stuck session (no active call needed) ────────────────
  const forceEndSession = useCallback(async (session) => {
    try {
      await fetch(`${API}/sessions/${session.sessionId}`, { method: "DELETE" });
      // Immediately refresh the queue so the card disappears
      await fetchQueue();
    } catch (err) {
      setQueueError(`Failed to end session: ${err.message}`);
    }
  }, [fetchQueue]);

  // ── End call — doctor clicks "End Visit" ──────────────────────────────
  const endCall = useCallback(async () => {
    if (!activeSession) return;

    try {
      // Tell the backend to delete the Chime meeting + mark ENDED
      await fetch(`${API}/sessions/${activeSession.sessionId}`, {
        method: "DELETE",
      });
    } catch (err) {
      console.warn("endSession API call failed:", err.message);
    }

    try {
      await meetingManager.leave();
    } catch (_) { /* already left */ }

    setCallStatus("idle");
    setActive(null);

    // Resume polling
    fetchQueue();
    pollRef.current = setInterval(fetchQueue, POLL_INTERVAL);
  }, [activeSession, meetingManager, fetchQueue]);

  // ── Send invite ───────────────────────────────────────────────────────
  const sendInvite = useCallback(async (formData) => {
    if (!API) {
      throw new Error(
        "VITE_API_URL is not set. Copy doctor-app/.env.example to .env, " +
        "add your API Gateway URL, then restart the dev server."
      );
    }

    let res;
    try {
      res = await fetch(`${API}/invite`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ...formData, doctorId: DOCTOR_ID }),
      });
    } catch (networkErr) {
      // fetch() itself threw — network unreachable, CORS preflight blocked, etc.
      throw new Error(
        `Network error reaching ${API}/invite — ` +
        "check that the backend is deployed and VITE_API_URL is correct. " +
        `(${networkErr.message})`
      );
    }

    let data = await res.json();

    data.joinUrl = data.joinUrl?.replace("https://patient.carelink.ksmtechnologies.net", APP_URL);

    if (!res.ok) throw new Error(data.error ?? `Server error ${res.status}`);
    return data; // { sessionId, joinUrl, message }
  }, []);

  return {
    sessions,
    activeSession,
    callStatus,
    queueError,
    callError,
    admitPatient,
    endCall,
    forceEndSession,
    sendInvite,
    refreshQueue: fetchQueue,
  };
}
